import {
  EIconButtonSize,
  EIconButtonVariant,
  GeneralArrowRightIcon,
  IconButton,
  Textarea,
} from '@outdoorsyco/bonfire';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useChatbot } from '../ChatbotProvider';

interface IChatbotInputProps {
  disableSend: boolean;
}

const MAX_CHAT_MESSAGE_LENGTH = 250;

const ChatbotInput = ({ disableSend }: IChatbotInputProps) => {
  const { isOpen, submitMessage } = useChatbot();
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const disableSendButton = useMemo(() => {
    return disableSend || inputValue.length > MAX_CHAT_MESSAGE_LENGTH;
  }, [disableSend, inputValue.length]);

  const onSubmitMessage = useCallback(
    (event: React.SyntheticEvent) => {
      event.preventDefault();

      if (disableSendButton) return;
      if (!inputValue) return;

      const message = inputValue.trim();
      if (message) {
        submitMessage(message);
      }

      setInputValue('');
      inputRef.current?.focus();
    },
    [inputValue, submitMessage, disableSendButton],
  );

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        onSubmitMessage(event);
      }
    },
    [onSubmitMessage],
  );

  return (
    <div className="px-4 pt-4 pb-0">
      <form onSubmit={onSubmitMessage}>
        <div className="flex flex-row items-center gap-2">
          <div className="flex-1">
            <Textarea
              ref={inputRef}
              className="flex-1"
              ariaLabel="Chat message"
              name="chatMessage"
              inputValue={inputValue}
              onChange={setInputValue}
              controlled
              maxLength={MAX_CHAT_MESSAGE_LENGTH}
              maxRows={3}
              minRows={1}
              onKeyDown={handleKeyDown}
            />
          </div>
          <IconButton
            type="submit"
            icon={GeneralArrowRightIcon}
            size={EIconButtonSize.Medium}
            variant={EIconButtonVariant.Quaternary}
            focusRing={false}
            disabled={disableSendButton}
          />
        </div>
      </form>
    </div>
  );
};

export default ChatbotInput;
